import { ChangeEventHandler } from 'react';
import { cn } from '../../../utils';
import ErrorOrHelp from '../ErrorOrHelp';
import { AcceptsSetFormItemError } from '@types';

export type RadioGroupProps = {
    title?: string;
    subtitle?: string;
    name: string;
    options: RadioOption[];
    className?: string | object;
    value?: string;
    screenReaderLabel?: string;
    error?: string;
    onChange: ChangeEventHandler<HTMLInputElement>;
    onBlur?: ChangeEventHandler<HTMLInputElement>;
} & AcceptsSetFormItemError;

export type RadioOption = {
    id: string;
    value: string;
    label: string;
    description?: string;
};

export default function RadioGroup({
    title,
    subtitle,
    name,
    options,
    className,
    value,
    screenReaderLabel,
    onChange,
    error,
    onBlur,
    ...rest
}: RadioGroupProps) {
    return (
        <div>
            {title && (
                <label className="alli-text-base alli-font-medium alli-text-blue-gray-700">
                    {title}
                </label>
            )}
            {subtitle && (
                <p className="alli-text-sm alli-text-gray-700">{subtitle}</p>
            )}

            <fieldset {...rest} className="alli-mt-4">
                {screenReaderLabel && (
                    <legend className="alli-sr-only">
                        {screenReaderLabel}
                    </legend>
                )}
                <div className={cn('alli-space-y-5', className)}>
                    {options?.map(option => (
                        <div
                            key={option.id}
                            className="alli-relative alli-flex alli-items-start">
                            <div className="alli-flex alli-h-6 alli-items-center">
                                <input
                                    id={option.id}
                                    aria-describedby={`${option.id}-description`}
                                    name={name}
                                    type="radio"
                                    defaultChecked={option.value === value}
                                    className={cn(
                                        'alli-h-4 alli-w-4 alli-border-gray-700 alli-text-blue-600 before:alli-text-green-700 focus:alli-ring-blue-600',
                                        { 'alli-border-red-400': error },
                                    )}
                                    data-testid={option.id}
                                    value={option.value}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                />
                            </div>
                            <div className="alli-ml-3 alli-text-sm alli-leading-6">
                                <label
                                    htmlFor={option.id}
                                    className="alli-font-medium alli-text-blue-gray-700">
                                    {option.label}
                                </label>
                                {option?.description && (
                                    <p
                                        id={`${option.id}-description`}
                                        className="alli-text-gray-700">
                                        {option.description}
                                    </p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
                {error && <ErrorOrHelp error={error} name={name} />}
            </fieldset>
        </div>
    );
}
